/**
 * Consent helpers — read/write the `siteTerms` and `marketing` objects on ClientsV2 docs.
 *
 * During the migration from the legacy flat `tos*` fields, the readers here are DUAL-READ:
 * they accept the new nested shape OR the legacy fields. Writers always produce the new shape;
 * the marketing popup additionally dual-writes legacy fields until the cron is migrated.
 */
import { serverTimestamp } from 'firebase/firestore'
import { CONSENT_SOURCE } from '@core/config/consentConstants.js'

/** True when the client may receive marketing content (WhatsApp / email / notifications). */
export function hasMarketingConsent(client) {
  if (!client) return false
  if (client.marketing && typeof client.marketing === 'object') {
    return client.marketing.consent === true
  }
  return client.tos === true
}

/** True when a real person (client or admin) recorded a Yes/No — never re-ask. */
export function hasMarketingAnswer(client) {
  if (!client) return false
  if (client.marketing?.answered === true) return true
  return client.tos_answered === true
}

/** Marketing display state: 'approved' | 'declined' | 'pending' */
export function getMarketingState(client) {
  if (hasMarketingConsent(client)) return 'approved'
  if (hasMarketingAnswer(client)) return 'declined'
  return 'pending'
}

/** True when the client accepted the site terms (תקנון + גיל 18). No versioning. */
export function hasAcceptedSiteTerms(client) {
  return client?.siteTerms?.accepted === true
}

/**
 * True when the customer removed themselves through the WhatsApp unsubscribe link and is
 * still out. Gated on the source so a later admin decision (with its own date) is not
 * mislabelled as a self-unsubscribe.
 */
export function isUnsubscribedViaWhatsapp(client) {
  return (
    !hasMarketingConsent(client) &&
    client?.marketing?.source === CONSENT_SOURCE.WHATSAPP_UNSUBSCRIBE &&
    !!client?.marketing?.unsubscribedAt
  )
}

/** When the customer last unsubscribed via WhatsApp (Firestore Timestamp), or null. */
export function getUnsubscribedAt(client) {
  return client?.marketing?.unsubscribedAt || null
}

/**
 * Nested `marketing` object to write on a ClientsV2 doc.
 * `previous` is the client's CURRENT `marketing` object: the write replaces the whole
 * object, so `unsubscribedAt` is carried through here — otherwise a re-approval would
 * silently erase when the customer unsubscribed.
 */
export function buildMarketingConsentPayload({
  consent,
  uid,
  source = CONSENT_SOURCE.APP,
  previous = null,
}) {
  const payload = {
    consent: consent === true,
    answered: true,
    answeredAt: serverTimestamp(),
    answeredBy: uid || null,
    source,
  }
  if (previous?.unsubscribedAt) payload.unsubscribedAt = previous.unsubscribedAt
  return payload
}

/**
 * Dotted-path update for the `marketing` object — the form every WRITER should use.
 * `updateDoc` with dotted keys MERGES into the map, so `marketing.unsubscribedAt`
 * (set only by the unsubscribe function) is never clobbered, whoever writes.
 */
export function buildMarketingConsentUpdate({ consent, uid, source = CONSENT_SOURCE.APP }) {
  return {
    'marketing.consent': consent === true,
    'marketing.answered': true,
    'marketing.answeredAt': serverTimestamp(),
    'marketing.answeredBy': uid || null,
    'marketing.source': source,
  }
}

/** Nested `siteTerms` object to write on a ClientsV2 doc. */
export function buildSiteTermsPayload({ uid, source = CONSENT_SOURCE.APP }) {
  return {
    accepted: true,
    acceptedAt: serverTimestamp(),
    acceptedBy: uid || null,
    source,
  }
}
